'use client';

import { useEffect, useRef } from 'react';

interface WaveformVisualizerProps {
  waveformData: number[];
  isRecording: boolean;
  isPaused: boolean;
  width?: number;
  height?: number;
}

export function WaveformVisualizer({
  waveformData,
  isRecording,
  isPaused,
  width = 400,
  height = 100
}: WaveformVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    const centerY = height / 2;

    // Draw center line
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(width, centerY);
    ctx.stroke();

    if (waveformData.length === 0) {
      return;
    }

    // Set bar color to match app's primary color
    ctx.fillStyle = '#2D5A3E'; // primary-700

    // Calculate bar width and spacing for thin bars
    const barWidth = 2; // Thin bars
    const barSpacing = 3; // Space between bars
    const totalBarWidth = barWidth + barSpacing;
    const numBars = Math.floor(width / totalBarWidth);

    // Get the most recent data points (scrolling from right to left)
    const startIndex = Math.max(0, waveformData.length - numBars);
    const visibleData = waveformData.slice(startIndex);

    // Draw waveform bars from right to left (newest on the right)
    visibleData.forEach((value, index) => {
      // Normalize the value (0-128 range from getByteTimeDomainData)
      const normalizedValue = value / 128;
      const barHeight = normalizedValue * (height * 0.8); // Use 80% of height for better appearance

      // Position bars from right to left
      const x = width - ((visibleData.length - index) * totalBarWidth);

      if (x >= 0) {
        // Draw symmetrical bars above and below center
        // Upper bar
        ctx.fillRect(x, centerY - barHeight / 2, barWidth, barHeight / 2);
        // Lower bar
        ctx.fillRect(x, centerY, barWidth, barHeight / 2);
      }
    });
  }, [waveformData, isRecording, isPaused, width, height]);

  return (
    <div className="flex items-center justify-center p-4 bg-white rounded-lg border border-gray-200">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="rounded"
        style={{ maxWidth: '100%' }}
      />
    </div>
  );
}